import { useCallback, useMemo, useRef } from "react";

import {
  faArchive,
  faExclamationCircle,
  faLink,
  faMagnifyingGlass,
  faSuitcase,
} from "@fortawesome/pro-regular-svg-icons";
import {
  createColumnHelper,
  DisplayColumnDef,
  HeaderContext,
} from "@tanstack/react-table";
import { isEmpty } from "lodash-es";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { Checkbox } from "src/base-components/Checkbox";
import { DisplayDateFromNow } from "src/base-components/Date";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { MENU_DIVIDER } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import {
  Cell,
  ColumnVisibilityDropdown,
  Header,
  TableComp as Table,
} from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import {
  useResetURLParamsFilters,
  useURLParamsFilters,
} from "src/base-components/tableColumnFilters/useFilters";
import { CaseAssignee } from "src/cases/common/CaseAssignee";
import { CaseStatusPill } from "src/cases/common/CaseStatusPill";
import {
  deserializeCaseTemplates,
  TemplateStatus,
  useAssignCase,
  useIsQueueMember,
  useQueueCaseTemplates,
  useTemplateStatus,
} from "src/cases/hooks";
import { useBulkCaseUpdate } from "src/cases/queries/cases";
import { useQueueCases } from "src/cases/queries/queues";
import { useCaseTemplatesRaw } from "src/cases/queries/templates";
import { useCasesTableContext } from "src/cases/queues/CasesTableContext";
import {
  AssigneeFilter,
  CasePropertyFilter,
  EqColumnFilter,
} from "src/cases/queues/CasesTableFilters";
import {
  CasePropertyValue,
  CaseTemplateStatusValue,
  CaseTemplateValue,
} from "src/cases/queues/CasesTableValues";
import { useEntityDisplayMap } from "src/cases/queues/hooks/useEntityDisplayMap";
import {
  CasePropertySchema,
  CaseQueueColumnKey,
  CaseQueueColumnSchemas,
  CaseQueueColumnSchemaWithOptions,
  DeserializedCaseTemplateWithVersions,
  DeserializedQueueCase,
  EnrichedDeserializedQueueCase,
} from "src/cases/queues/types";
import {
  enrichCaseWithColumnValues,
  extractQueueColumnSchemas,
  RESERVED_PROPERTY_KEYS,
} from "src/cases/queues/utils";
import { useCaseContextParams } from "src/cases/useCaseContextParams";
import { getPropertyVariant, onArchiveCasesError } from "src/cases/utils";
import { EmptyState } from "src/design-system/EmptyState";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import {
  CasesPageParams,
  getBaseUrl,
  getUrlToCaseViewPage,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { copyTextToClipboard } from "src/utils/clipboard";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

const helper = createColumnHelper<DeserializedQueueCase>();

type BuildColumnParams = {
  columnSchemas: CaseQueueColumnSchemas;
  customColumnKeys: CaseQueueColumnKey[];
  caseTemplates: DeserializedCaseTemplateWithVersions[];
  environment: Environment;
  showSelectorColumn: boolean;
  isSelectionDisabled?: boolean;
  getEntityDisplayValue: (entityId: string) => string;
};

const buildColumns = ({
  customColumnKeys,
  columnSchemas,
  caseTemplates,
  environment,
  showSelectorColumn,
  isSelectionDisabled = false,
  getEntityDisplayValue,
}: BuildColumnParams) => {
  const casePropertyColumns = customColumnKeys
    ?.map((property) => {
      const columnSchema = columnSchemas?.[property];
      if (!columnSchema) return null;

      return helper.accessor((row) => row, {
        id: property,
        meta: {
          displayName: columnSchema.display_name,
        },
        header: (info) => (
          <HeaderWithFilter
            dataLoc={`cases-table-header-${columnSchema.__key}`}
            info={info}
            title={columnSchema.display_name}
          >
            <CasePropertyFilter columnSchema={columnSchema} />
          </HeaderWithFilter>
        ),
        cell: (info) => {
          const caseData = info.row.original;

          const template = caseTemplates.find(
            (t) => t.id === caseData.template.id,
          );
          const version = template?.versions.find(
            (v) => v.id === caseData.template_version.id,
          );

          if (!template || !version) return null;

          const propertyKey = columnSchema.__key;
          const propertySchema = version?.case_properties?.[propertyKey] as
            | CasePropertySchema
            | undefined;
          const propertyValue = caseData?.case_properties[propertyKey];

          const entityDisplayName =
            columnSchema.type === "entity_ref" &&
            typeof propertyValue === "string"
              ? getEntityDisplayValue(propertyValue) || propertyValue
              : undefined;

          return (
            <Cell
              dataLoc={`cases-table-cell-${columnSchema.__key}-${info.row.id}`}
              info={info}
            >
              <CasePropertyValue
                columnSchema={columnSchema}
                entityDisplayName={entityDisplayName}
                environment={environment}
                propertySchema={propertySchema}
                propertyValue={propertyValue}
              />
            </Cell>
          );
        },
        maxSize: 350,
      });
    })
    .filter(Boolean) as DisplayColumnDef<
    EnrichedDeserializedQueueCase,
    unknown
  >[];

  const caseStatusColumn = helper.accessor((row) => row, {
    id: "status",
    meta: { displayName: "Status" },
    header: (info) => {
      const columnSchema = Object.values(columnSchemas).find(
        (schema) => schema?.__key === "status",
      ) as CaseQueueColumnSchemaWithOptions | undefined;

      const filterOptions = columnSchema?.options ?? [];

      return (
        <HeaderWithFilter
          dataLoc="cases-table-header-status"
          info={info}
          title="Status"
        >
          <EqColumnFilter
            dataLoc="cases-table-filter-status"
            displayName={info.column.id}
            elements={filterOptions.map((status) => ({
              key: status,
              value: status,
            }))}
            property={info.column.id}
          />
        </HeaderWithFilter>
      );
    },
    cell: (info) => {
      const caseData = info.row.original;

      const template = caseTemplates.find((t) => t.id === caseData.template.id);
      const version = template?.versions.find(
        (v) => v.id === caseData.template_version.id,
      );

      const variant = getPropertyVariant(
        caseData.case_properties.status,
        version?.case_properties?.status as CasePropertySchema,
      );

      return (
        <Cell dataLoc={`cases-table-cell-status-${info.row.id}`} info={info}>
          <CaseStatusPill
            status={caseData.case_properties?.status}
            subStatus={caseData.case_properties?.sub_status}
            variant={variant}
          />
        </Cell>
      );
    },
    maxSize: 350,
  });

  const caseTemplateColumns = [
    helper.accessor("template.id", {
      meta: { displayName: "Template" },
      header: (info) => (
        <Header dataLoc="cases-table-header-template" info={info}>
          Template
        </Header>
      ),
      cell: (info) => {
        const caseData = info.row.original;

        const template = caseTemplates.find(
          (t) => t.id === caseData.template.id,
        );
        const version = template?.versions.find(
          (v) => v.id === caseData.template_version.id,
        );
        if (!template || !version) return null;

        return (
          <Cell
            className="flex gap-x-1"
            dataLoc={`cases-table-cell-template-${info.row.id}`}
            info={info}
          >
            <CaseTemplateValue
              templateIcon={template.icon}
              templateId={template.id}
              templateName={template.template_name}
              versionId={version.id}
              versionName={version.version_name}
            />
          </Cell>
        );
      },
      maxSize: 250,
    }),
    helper.accessor(
      (row) => ({
        templateId: row.template?.id,
        schemaVersion: row.template_schema_version,
        versionId: row.template_version?.id,
        queueId: row.queue?.id,
      }),
      {
        id: "template_status",
        meta: { displayName: "Template Status" },
        header: (info) => (
          <Header info={info}>
            <span className="whitespace-nowrap">Template Status</span>
          </Header>
        ),
        cell: (info) => (
          <Cell info={info}>
            <CaseTemplateStatusValue value={info.getValue()} />
          </Cell>
        ),
      },
    ),
  ];

  const caseDatesColumns = [
    helper.accessor("created_at", {
      meta: { displayName: "Created at" },
      header: (info) => (
        <Header dataLoc="cases-table-header-created-at" info={info}>
          <span className="whitespace-nowrap">Created At</span>
        </Header>
      ),
      cell: (info) => (
        <Cell className="font-inter-normal-12px" info={info}>
          <DisplayDateFromNow value={info.getValue()} />
        </Cell>
      ),
    }),
    helper.accessor("updated_at", {
      meta: { displayName: "Updated at" },
      header: (info) => (
        <Header dataLoc="cases-table-header-updated-at" info={info}>
          <span className="whitespace-nowrap">Updated At</span>
        </Header>
      ),
      cell: (info) => (
        <Cell className="font-inter-normal-12px" info={info}>
          <DisplayDateFromNow value={info.getValue()} />
        </Cell>
      ),
    }),
  ];

  const caseAssigneeColumn = helper.accessor("assignee", {
    meta: { displayName: "Assigned to" },
    header: (info) => (
      <HeaderWithFilter
        dataLoc="cases-table-header-assigned-to"
        info={info}
        title="Assigned to"
      >
        <AssigneeFilter />
      </HeaderWithFilter>
    ),
    cell: (info) => {
      const data = info.row.original;

      return (
        <Cell info={info}>
          <div onClick={(e) => e.stopPropagation()}>
            <CaseAssignee
              assignee={data.assignee?.id}
              caseId={data.id}
              etag={data.etag}
              queueId={data.queue?.id}
              templateId={data.template?.id ?? ""}
              templateSchemaVersion={data.template_schema_version}
              templateVersionId={data.template_version?.id ?? ""}
            />
          </div>
        </Cell>
      );
    },
    maxSize: 200,
  });

  const idColumn = helper.accessor("id", {
    meta: { displayName: "Case ID" },
    header: (info) => (
      <Header dataLoc="cases-table-header-id" info={info}>
        <span className="whitespace-nowrap">Case ID</span>
      </Header>
    ),
    cell: (info) => (
      <Cell className="w-24" info={info}>
        <TruncatedIdPill id={info.getValue()} />
      </Cell>
    ),
  });

  const selectorColumn = helper.display({
    id: "select",
    meta: { sticky: "left" },
    header: (info) => (
      <Header dataLoc="cases-table-header-select" info={info}>
        <Checkbox
          checked={info.table.getIsAllRowsSelected()}
          disabled={isSelectionDisabled}
          indeterminate={info.table.getIsSomePageRowsSelected()}
          onChange={info.table.getToggleAllRowsSelectedHandler()}
        />
      </Header>
    ),
    cell: (info) => (
      <Cell
        className="w-8 items-center"
        dataLoc={`cases-table-cell-select-${info.row.id}`}
        info={info}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={info.row.getIsSelected()}
            disabled={!info.row.getCanSelect() || isSelectionDisabled}
            onChange={info.row.getToggleSelectedHandler()}
          />
        </div>
      </Cell>
    ),
  });

  const actionsColumn = helper.display({
    id: "actions",
    header: (info) => (
      <Header dataLoc="cases-table-header-actions" info={info}>
        <div className="flex w-13.5 justify-end bg-white pr-1.5">
          <ColumnVisibilityDropdown min={3} table={info.table} />
        </div>
      </Header>
    ),
    cell: (info) => {
      const data = info.row.original;

      return (
        <Cell className="items-center justify-end" info={info}>
          <CaseActionsMenu
            assigneeId={data.assignee?.id}
            caseId={data.id}
            etag={data.etag}
            templateId={data.template?.id ?? ""}
            templateSchemaVersion={data.template_schema_version}
            versionId={data.template_version?.id ?? ""}
          />
        </Cell>
      );
    },
    size: 40,
    maxSize: 40,
    meta: { sticky: "right" },
  });

  return [
    ...(showSelectorColumn ? [selectorColumn] : []),
    idColumn,
    ...caseTemplateColumns,
    ...casePropertyColumns,
    caseStatusColumn,
    caseAssigneeColumn,
    ...caseDatesColumns,
    actionsColumn,
  ] as DisplayColumnDef<EnrichedDeserializedQueueCase, unknown>[];
};

const HeaderWithFilter: React.FC<{
  info: HeaderContext<EnrichedDeserializedQueueCase, unknown>;
  title: string;
  children: React.ReactNode;
  dataLoc?: string;
}> = ({ info, title, children, dataLoc }) => (
  <Header dataLoc={dataLoc} info={info}>
    <div className="flex w-full flex-row justify-between gap-x-1 whitespace-nowrap align-middle capitalize">
      {title}
      {children}
    </div>
  </Header>
);

type CaseActionsMenuProps = {
  caseId: string;
  templateSchemaVersion: number;
  templateId: string;
  versionId: string;
  etag: string;
  assigneeId?: string;
};

const CaseActionsMenu: React.FC<CaseActionsMenuProps> = ({
  caseId,
  etag,
  templateId,
  versionId,
  assigneeId,
  templateSchemaVersion,
}) => {
  const { wsId, orgId } = useParamsDecode<CasesPageParams>();
  const { queue_id } = useParamsDecode<CasesPageParams>();
  const [environment] = useEnvironment();

  const currentUserId = useCurrentUserId();
  const { onAssign: assignCase } = useAssignCase(queue_id);
  const { mutateAsync: updateCases } = useBulkCaseUpdate();
  const { status } = useTemplateStatus({
    templateId,
    versionId,
    queueId: queue_id,
    schemaVersion: templateSchemaVersion,
  });

  const isOutdated = status === TemplateStatus.Outdated;

  const onCopyLink = async () => {
    try {
      await copyTextToClipboard(
        getBaseUrl() + getUrlToCaseViewPage(orgId, wsId, caseId),
      );
      toastActions.success({
        title: "Link copied to clipboard",
      });
    } catch {
      toastActions.failure({
        title: "Failed to copy link",
      });
    }
  };

  const handleArchiveCase = useCallback(async () => {
    if (!caseId) return;

    try {
      await updateCases({
        queueId: queue_id,
        operation: "archive",
        caseIds: [caseId],
      });
    } catch (error) {
      logger.error("Error archiving case", error);
      onArchiveCasesError(error);
    }
  }, [updateCases, caseId, queue_id]);

  const isAssignedToCurrentUser = assigneeId === currentUserId;

  return (
    <EllipsisOptionsDropdown
      buttonClassName="opacity-0 group-hover:opacity-100"
      dataLoc="case-row-actions"
      elements={[
        {
          key: "Copy case link",
          icon: faLink,
          action: onCopyLink,
        },
        {
          key: "Claim case",
          icon: faSuitcase,
          disabled: isOutdated || isAssignedToCurrentUser,
          action: () =>
            assignCase({
              etag,
              caseId,
              assigneeId: currentUserId,
            }),
        },
        ...(environment === "sandbox"
          ? [
              MENU_DIVIDER,
              {
                key: "Archive case",
                icon: faArchive,
                action: handleArchiveCase,
              },
            ]
          : []),
      ]}
      iconSize="sm"
      placement="bottom start"
    />
  );
};

export const CasesTable: React.FC = () => {
  const navigate = useNavigate();
  const [environment] = useEnvironment();
  const { onSelectRow, selectedRows, isSelectionDisabled } =
    useCasesTableContext();
  const resetFilters = useResetURLParamsFilters();
  const filters = useURLParamsFilters();
  const currentUserId = useCurrentUserId();
  const { cases: casesCapabilities } = useCapabilities();
  const { orgId, wsId, queue_id: queueId } = useParamsDecode<CasesPageParams>();
  const { filterParams } = useCaseContextParams();
  const isQueueMember = useIsQueueMember({ userId: currentUserId, queueId });
  const canInteractWithCases = casesCapabilities.canManage || isQueueMember;

  const {
    data,
    isLoading: isLoadingQueueCases,
    isError,
    fetchNextPage,
    isFetchingNextPage,
  } = useQueueCases({
    queueId,
    environment,
    filters,
  });

  const { data: caseQueueTemplates, isLoading: isLoadingCaseQueueTemplates } =
    useQueueCaseTemplates(queueId);

  const { data: caseTemplates } = useCaseTemplatesRaw({
    select: deserializeCaseTemplates,
  });

  const columnSchemas = useMemo(
    () =>
      extractQueueColumnSchemas(
        caseQueueTemplates?.flatMap((template) => template.versions),
      ),
    [caseQueueTemplates],
  );

  const cases = useMemo(
    () =>
      data?.map((caseData) =>
        enrichCaseWithColumnValues(caseData, columnSchemas),
      ) ?? [],
    [data, columnSchemas],
  );

  const customColumnKeys = useMemo(() => {
    const columnKeys = Object.keys(columnSchemas) as CaseQueueColumnKey[];

    return columnKeys.filter((key) => {
      const columnSchema = columnSchemas[key];
      const propertyKey = columnSchema?.__key;

      return propertyKey && !RESERVED_PROPERTY_KEYS.includes(propertyKey);
    });
  }, [columnSchemas]);

  const isLoading = isLoadingQueueCases || isLoadingCaseQueueTemplates;

  const { displayMap } = useEntityDisplayMap({
    cases,
    columnSchemas,
    caseTemplates: caseTemplates?.data ?? [],
    environment,
    enabled: !isLoading && cases.length > 0,
  });

  // Use ref to keep a stable reference to displayMap
  const entityDisplayMapRef = useRef<Record<string, string>>({});
  entityDisplayMapRef.current = displayMap;

  const columns = useMemo(() => {
    return buildColumns({
      customColumnKeys,
      columnSchemas,
      caseTemplates: caseTemplates?.data ?? [],
      environment,
      showSelectorColumn: casesCapabilities.canAccess,
      isSelectionDisabled,
      getEntityDisplayValue: (id: string): string =>
        entityDisplayMapRef.current[id],
    });
  }, [
    columnSchemas,
    customColumnKeys,
    caseTemplates,
    environment,
    casesCapabilities.canAccess,
    isSelectionDisabled,
  ]);

  const isCasesError = !isLoading && isError;
  const hasCases = !isLoading && !!cases.length;
  const isCasesEmpty = !isLoading && !isCasesError && !hasCases;
  const isCasesFilterEmpty = isEmpty(filters);

  return (
    <FloatingWindowsProvider>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4">
        <Table
          columnVisibility={{
            template_status: false,
            template_id: false,
          }}
          columns={columns}
          data={cases}
          dataLoc="cases-table"
          frameClassName={twJoin(
            "w-full pb-2",
            (isCasesEmpty || isCasesError) && "h-auto",
          )}
          getRowId={(row) => row.id}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          rowClassName={twJoin(
            canInteractWithCases && "cursor-pointer hover:bg-gray-50",
          )}
          rowPropsGetter={(row) =>
            canInteractWithCases
              ? {
                  "data-loc": `queue-case-row-${row.id}`,
                  onClick: () => {
                    navigate(
                      getUrlToCaseViewPage(orgId, wsId, row.id, {
                        ...filterParams,
                        ...(queueId ? { queueId } : {}),
                      }),
                    );
                  },
                }
              : {}
          }
          rowSelection={selectedRows}
          uiStoreKey={`${wsId}_${queueId}_cases_visible_columns`}
          variant="compact"
          onRowSelectionChange={onSelectRow}
          onScrolledToTheBottom={fetchNextPage}
        />
        {isCasesError && (
          <div className="h-96">
            <EmptyState
              actionButton={{
                text: "Clear filters",
                onClick: resetFilters,
              }}
              description="We're having trouble loading cases. Try clearing the filters."
              headline="Error loading cases"
              icon={faExclamationCircle}
              variant="error"
            />
          </div>
        )}
        {isCasesEmpty && (
          <div className="h-96">
            {isCasesFilterEmpty ? (
              <EmptyState
                description="No cases found in this queue"
                headline="No cases found"
                icon={faMagnifyingGlass}
              />
            ) : (
              <EmptyState
                actionButton={{
                  text: "Clear filters",
                  onClick: resetFilters,
                }}
                description="There are no cases matching your current filters. Try clearing the filters."
                headline="No cases for the current filters"
                icon={faMagnifyingGlass}
              />
            )}
          </div>
        )}
      </div>
    </FloatingWindowsProvider>
  );
};
